import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Modal } from '@/components/ui/modal';
import { DataTable, type Column } from '@/components/data-table/data-table';
import { Pagination } from '@/components/data-table/pagination';
import { KvDiff, KvList } from '@/components/ui/kv-diff';
import { queryKeys } from '@/lib/query-keys';
import { formatDateTime, formatRelative } from '@/lib/format';
import { getAuditFeed } from '@/features/audit/api';
import { actorLabel, type AdminAuditEntry } from '@/features/audit/types';

const PAGE_SIZE = 50;

interface AuditFilters {
  entity_type: string;
  action: string;
  actor_id: string;
  since: string;
  until: string;
}

const EMPTY: AuditFilters = { entity_type: '', action: '', actor_id: '', since: '', until: '' };

export const Route = createFileRoute('/_authenticated/audit/')({
  component: AuditPage,
});

function AuditPage() {
  const [draft, setDraft] = useState<AuditFilters>(EMPTY);
  const [applied, setApplied] = useState<AuditFilters>(EMPTY);
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<AdminAuditEntry | null>(null);

  const filters = {
    page,
    limit: PAGE_SIZE,
    entity_type: applied.entity_type || undefined,
    action: applied.action || undefined,
    actor_id: applied.actor_id || undefined,
    since: applied.since ? new Date(applied.since).toISOString() : undefined,
    until: applied.until ? new Date(applied.until).toISOString() : undefined,
  };

  const query = useQuery({
    queryKey: queryKeys.audit.feed(filters),
    queryFn: () => getAuditFeed(filters),
    placeholderData: (prev) => prev,
  });

  function apply() {
    setApplied(draft);
    setPage(1);
  }

  function reset() {
    setDraft(EMPTY);
    setApplied(EMPTY);
    setPage(1);
  }

  const columns: Column<AdminAuditEntry>[] = [
    {
      key: 'when',
      header: 'When',
      render: (e) => (
        <div>
          <div className="wb-small">{formatRelative(e.created_at)}</div>
          <div className="wb-tiny">{formatDateTime(e.created_at)}</div>
        </div>
      ),
    },
    {
      key: 'actor',
      header: 'Actor',
      render: (e) => (
        <div>
          <div style={{ fontWeight: 600 }}>{actorLabel(e)}</div>
          {e.actor_email ? <div className="wb-tiny">{e.actor_email}</div> : null}
        </div>
      ),
    },
    { key: 'action', header: 'Action', render: (e) => <Badge tone="info">{e.action}</Badge> },
    {
      key: 'entity',
      header: 'Entity',
      render: (e) => (
        <div>
          <div className="wb-small">{e.entity_type}</div>
          <div className="wb-tiny wb-mono">{e.entity_id}</div>
        </div>
      ),
    },
    {
      key: 'summary',
      header: 'Summary',
      render: (e) => <span className="wb-small">{e.summary || '—'}</span>,
    },
  ];

  return (
    <div className="mx-auto max-w-6xl p-8">
      <div className="flex flex-col gap-1.5" style={{ marginBottom: 20 }}>
        <span className="wb-eyebrow">Operations</span>
        <h1 className="wb-h1">Audit log</h1>
        <p className="wb-sub">Every admin action across the platform, with before/after detail.</p>
      </div>

      <div className="wb-toolbar" style={{ marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <input
          className="wb-field"
          style={{ width: 150 }}
          placeholder="Entity type"
          value={draft.entity_type}
          onChange={(e) => setDraft({ ...draft, entity_type: e.target.value })}
        />
        <input
          className="wb-field"
          style={{ width: 150 }}
          placeholder="Action"
          value={draft.action}
          onChange={(e) => setDraft({ ...draft, action: e.target.value })}
        />
        <input
          className="wb-field"
          style={{ width: 180 }}
          placeholder="Actor id"
          value={draft.actor_id}
          onChange={(e) => setDraft({ ...draft, actor_id: e.target.value })}
        />
        <input
          className="wb-field"
          type="date"
          style={{ width: 150 }}
          value={draft.since}
          onChange={(e) => setDraft({ ...draft, since: e.target.value })}
        />
        <input
          className="wb-field"
          type="date"
          style={{ width: 150 }}
          value={draft.until}
          onChange={(e) => setDraft({ ...draft, until: e.target.value })}
        />
        <Button size="sm" onClick={apply}>
          Apply
        </Button>
        <Button size="sm" variant="ghost" onClick={reset}>
          Reset
        </Button>
      </div>

      <DataTable
        columns={columns}
        rows={query.data?.entries}
        rowKey={(e) => e.id}
        isLoading={query.isLoading}
        error={query.error}
        onRetry={() => query.refetch()}
        onRowClick={(e) => setSelected(e)}
        empty={{ title: 'No audit entries', hint: 'Try widening the filters.' }}
      />

      {query.data && query.data.total > 0 ? (
        <Pagination page={page} limit={PAGE_SIZE} total={query.data.total} onPageChange={setPage} />
      ) : null}

      <Modal
        open={selected !== null}
        onClose={() => setSelected(null)}
        title={selected ? `${selected.action} · ${selected.entity_type}` : 'Audit entry'}
      >
        {selected ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div className="wb-small">
              <strong>{actorLabel(selected)}</strong> · {formatDateTime(selected.created_at)}
            </div>
            {selected.summary ? <p className="wb-small">{selected.summary}</p> : null}
            <div>
              <div className="wb-eyebrow" style={{ marginBottom: 8 }}>
                Changes
              </div>
              <KvDiff before={selected.before} after={selected.after} />
            </div>
            {selected.metadata && Object.keys(selected.metadata).length > 0 ? (
              <div>
                <div className="wb-eyebrow" style={{ marginBottom: 8 }}>
                  Metadata
                </div>
                <KvList data={selected.metadata} />
              </div>
            ) : null}
          </div>
        ) : null}
      </Modal>
    </div>
  );
}
